import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { QrCode } from "lucide-react";

// Kiosk-style office QR display (display-only permission). Auto-refreshes the QR when it expires.
export default function AttendanceQrDisplay() {
  const [params] = useSearchParams();
  const [displays, setDisplays] = useState([]);
  const [configId, setConfigId] = useState(params.get("config") || "");
  const [qr, setQr] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [err, setErr] = useState(null);
  const ivRef = useRef(null);

  useEffect(() => {
    api.get("/hr/attendance/qr/displays").then(r => {
      setDisplays(r.data);
      setConfigId(cur => cur || (r.data[0] ? r.data[0].id : ""));
      if (r.data.length === 0) setErr("No active QR configuration. Enable one under HR → Attendance → QR Verification.");
    }).catch(() => setErr("Not authorised to view the QR display."));
  }, []);

  const fetchQr = useCallback(() => {
    if (!configId) return;
    api.get(`/hr/attendance/qr/current?config_id=${configId}`).then(r => { setQr(r.data); setErr(null); })
      .catch(() => setErr("Unable to load QR (configuration inactive?)."));
  }, [configId]);

  useEffect(() => { fetchQr(); }, [fetchQr]);

  useEffect(() => {
    if (!qr) return;
    setRemaining(qr.validity_seconds);
    ivRef.current = setInterval(() => {
      setRemaining(s => {
        if (s <= 1) { clearInterval(ivRef.current); fetchQr(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(ivRef.current);
  }, [qr, fetchQr]);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-6" data-testid="qr-display-page">
      {displays.length > 1 && (
        <select value={configId} onChange={e => setConfigId(e.target.value)} className="mb-4 bg-slate-800 rounded px-3 py-1.5 text-sm" data-testid="qr-display-select">
          {displays.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      )}
      {err && <div className="text-amber-300 text-center max-w-md" data-testid="qr-display-error">{err}</div>}
      {qr && !err && (
        <>
          <div className="text-sky-300 uppercase tracking-widest text-sm mb-1">Attendance QR</div>
          <h1 className="text-2xl font-bold mb-1" data-testid="qr-display-name">{qr.config_name}</h1>
          {qr.location_name && <div className="text-slate-400 mb-4">{qr.location_name}</div>}
          <div className="bg-white p-4 rounded-2xl" data-testid="qr-display-image"><img src={qr.qr_png} alt="Attendance QR" className="w-72 h-72" /></div>
          <div className="mt-4 text-lg font-mono" data-testid="qr-display-countdown">Refreshes in {remaining}s</div>
          <div className="text-slate-500 text-sm mt-1">Scan the current code from your phone at clock in / out</div>
        </>
      )}
      {!qr && !err && <div className="text-slate-400 flex items-center gap-2"><QrCode className="w-6 h-6" /> Loading…</div>}
    </div>
  );
}
